import * as React from 'react';
import {ChangeEvent} from 'react';
import {useFormikContext} from 'formik';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {StyledContainer as BaseContainer} from 'containers/Container';
import Switch from 'components/Switch';
import {Title as ContainerTitle} from './Plan';
import RadioCard from './RadioCard';
import RangeStudents from './RangeStudents';
import RangeStorage from './RangeStorage';
import {AdditionalStudents, PaymentPeriod, TariffsValues} from "../types";
import {getStudentsCost, setLocalStorageTariffs} from "../helpers";
import Footer from "./Footer";

export const Wrapper = styled.div`
  display: grid;
  gap: 17px;
`;

export const Container = styled(BaseContainer)`
  min-width: 930px;
  padding: 20px;
  border-radius: 5px;
  box-sizing: border-box;
  background: ${({theme}) => theme.variables.accentBackgroundColor};
`;

export const ContainerDescription = styled.div`
  margin-bottom: 20px;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.01em;
  color: ${({theme}) => transparentize(.6, theme.variables.color)};
`;

export const Groups = styled.div`
  display: grid;
  gap: 20px;
`;

const Group = styled.div`
  
`;

export const Separator = styled.div`
  margin: 10px 0;
  width: 100%;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.05);
`;

export const GroupTitle = styled.div`
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.01em;
`;

const GroupContent = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
`;

const FooterTitle = styled.div`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 10px;
  font-size: 18px;
  font-weight: 500;
  line-height: 23px;
  letter-spacing: 0.01em;
`;

const AutoRenewalBox = styled.label`
  padding: 10px 0;
  margin: -5px 0;
  height: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

/**
 * get discount percent for tariff
 * @param {PaymentPeriod | undefined} period
 * @constructor
 */
export function getDiscount(period: PaymentPeriod | undefined) {
  switch (period) {
    case PaymentPeriod.MonthEnd: return 0;
    case PaymentPeriod.Month: return 20;
    case PaymentPeriod.ThreeMonth: return 30;
    case PaymentPeriod.SixMonth: return 40;
    case PaymentPeriod.Year: return 50;
    default: return 0;
  }
}

/**
 * get payment period for tariff
 * @param {PaymentPeriod | undefined} period
 * @constructor
 */
export function getPaymentPeriodValue(period: PaymentPeriod | undefined) {
  switch (period) {
    case PaymentPeriod.MonthEnd: return 1;
    case PaymentPeriod.Month: return 1;
    case PaymentPeriod.ThreeMonth: return 3;
    case PaymentPeriod.SixMonth: return 6;
    case PaymentPeriod.Year: return 12;
    default: return 1;
  }
}

/**
 * render tariffs constructor component
 * @constructor
 */
export default function Constructor(): React.ReactElement {
  const {values, setFieldValue} = useFormikContext<Partial<TariffsValues>>();

  React.useEffect(() => {
    if (values.selected) {
      setLocalStorageTariffs(values.selected)
    }
  }, [values.selected])

  const paymentPeriod = values.selected?.paymentPeriod;
  const additionalStudents = values.selected?.additionalStudents;
  const autoRenewal = values.selected?.autoRenewal;
  const prices = values.selected?.prices;

  /**
   * payment period item click handler
   * @param {PaymentPeriod} period
   * @constructor
   */
  function onPaymentPeriodClick(period: PaymentPeriod) {
    setFieldValue('selected.paymentPeriod', period);
  }

  /**
   * payment additional students item click handler
   * @param {AdditionalStudents} value
   * @constructor
   */
  function onStudentsCountClick(value: AdditionalStudents) {
    setFieldValue('selected.prices.students', getStudentsCost(value));
    setFieldValue('selected.additionalStudents', value)
  }

  /**
   * auto renewal change handler
   * @param {ChangeEvent<HTMLInputElement>} event
   * @constructor
   */
  function onAutoRenewalChange(event: ChangeEvent<HTMLInputElement>) {
    setFieldValue('selected.autoRenewal', Boolean(event.target.value));
  }

  React.useEffect(() => {
    const period = values.selected?.paymentPeriod;

    const priceForStorage = prices?.storage || 0;
    const priceForStudents= prices?.students || 0;

    const fullPrice = priceForStorage + priceForStudents;

    const discountValue = fullPrice * getDiscount(period) / 100;

    const value = (fullPrice - discountValue) * getPaymentPeriodValue(period);

    setFieldValue('selected.prices.full', Math.round(value));

  }, [
    prices?.students,
    prices?.storage,
    paymentPeriod
  ])

  /**
   * render tariff total amount
   * @constructor
   */
  function renderTotalAmount() {
    return (
      <FooterTitle>
        <span>Всего к оплате:</span>
        <span>{prices?.full} ₽</span>
      </FooterTitle>
    )
  }

  return (
    <Wrapper>
      <Container>
        <ContainerTitle>Конструктор тарифа</ContainerTitle>
        <ContainerDescription>
          Все функции доступны на всех тарифах.
        </ContainerDescription>
        <Groups>
          <Group>
            <GroupTitle>Количество активных участников:</GroupTitle>
            <RangeStudents />
          </Group>

          <Group>
            <GroupTitle>Если нужно больше участников:</GroupTitle>
            <GroupContent>
              <RadioCard
                label={'2 000'}
                checked={additionalStudents === 2000}
                onClick={() => onStudentsCountClick(2000)}
              />
              <RadioCard
                label={'3 000'}
                checked={additionalStudents === 3000}
                onClick={() => onStudentsCountClick(3000)}
              />
              <RadioCard
                label={'4 000'}
                checked={additionalStudents === 4000}
                onClick={() => onStudentsCountClick(4000)}
              />
              <RadioCard
                label={'5 000'}
                checked={additionalStudents === 5000}
                onClick={() => onStudentsCountClick(5000)}
              />
              <RadioCard
                label={'Неограничено'}
                checked={additionalStudents === 'Unlimited'}
                onClick={() => onStudentsCountClick('Unlimited')}
              />
            </GroupContent>
          </Group>

          <Group>
            <GroupTitle>Оплатить:</GroupTitle>
            <GroupContent>
              <RadioCard
                label={'на 1 месяц'}
                discountLabel={'скидка 20%'}
                checked={paymentPeriod === PaymentPeriod.Month}
                onClick={() => onPaymentPeriodClick(PaymentPeriod.Month)}
              />
              <RadioCard
                label={'на 3 месяца'}
                discountLabel={'скидка 30%'}
                checked={paymentPeriod === PaymentPeriod.ThreeMonth}
                onClick={() => onPaymentPeriodClick(PaymentPeriod.ThreeMonth)}
              />
              <RadioCard
                label={'на 6 месяцев'}
                discountLabel={'скидка 40%'}
                description={'* без ссылок на Grow Place'}
                checked={paymentPeriod === PaymentPeriod.SixMonth}
                onClick={() => onPaymentPeriodClick(PaymentPeriod.SixMonth)}
              />
              <RadioCard
                label={'на 1 год'}
                discountLabel={'скидка 50%'}
                description={'* без ссылок на Grow Place'}
                checked={paymentPeriod === PaymentPeriod.Year}
                onClick={() => onPaymentPeriodClick(PaymentPeriod.Year)}
              />
            </GroupContent>
          </Group>
        </Groups>
      </Container>

      <Container>
        <Groups>
          <Group>
            <GroupTitle>Файловое хранилище:</GroupTitle>
            <RangeStorage />
          </Group>

          <AutoRenewalBox>
            <span>Автопродление:</span>
            <Switch
              defaultChecked={autoRenewal}
              onChange={onAutoRenewalChange}
            />
          </AutoRenewalBox>

          {renderTotalAmount()}

          <Footer />
        </Groups>
      </Container>
    </Wrapper>
  );
}
